import { nextFiveAM } from '../StoreManagement/channelSnoozePresets';

export interface ItemSnoozePreset {
  /** Full label — desktop and bulk editor. */
  label: string;
  /** Compact label for mobile chips, where horizontal space is tight. */
  shortLabel: string;
  key: string;
  getValue: () => Date;
}

/**
 * Hard ceiling on how far ahead an item can be snoozed. This is a Byte Graph
 * server-side limit, not ours — it rejects expiries beyond this window. We
 * mirror it client-side so the calendar greys out unreachable dates instead of
 * letting users pick one and hit a server error on submit. Raising it requires
 * a Byte Graph API change.
 */
export const MAX_SNOOZE_DAYS = 60;

export const MAX_SNOOZE_HELPER_TEXT = `Items can be snoozed for up to ${MAX_SNOOZE_DAYS} days.`;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The latest expiry Byte Graph will accept, measured from now.
 *
 * Byte Graph measures the ceiling as elapsed time (60 x 24h). Day offsets are
 * therefore computed in milliseconds, not with setDate(): across a clock change
 * 60 calendar days is 60 x 24h +/- 1h, and the extra hour in autumn made every
 * 60-day snooze fail with BlackoutExpiryExceedsMaxError.
 */
export function maxSnoozeDate(from: Date = new Date()): Date {
  return new Date(from.getTime() + MAX_SNOOZE_DAYS * DAY_MS);
}

/** Clamp an expiry so it never lands past the 60-day ceiling. */
function capAtMax(date: Date): Date {
  const ceiling = maxSnoozeDate();
  return date > ceiling ? ceiling : date;
}

const addHours = (hours: number) => () => {
  const d = new Date();
  d.setHours(d.getHours() + hours);
  return capAtMax(d);
};

// Calendar days, so "1 Week" lands at the same wall-clock time. capAtMax keeps
// the 60-day preset inside Byte Graph's elapsed-time ceiling across clock changes.
const addDays = (days: number) => () => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return capAtMax(d);
};

/**
 * Quick expiry options, shared by the desktop item page, the mobile bottom
 * sheet and the bulk editor so all three offer the same durations.
 */
export const ITEM_SNOOZE_PRESETS: ItemSnoozePreset[] = [
  { label: '1 H', shortLabel: '1H', key: '1h', getValue: addHours(1) },
  { label: '2 H', shortLabel: '2H', key: '2h', getValue: addHours(2) },
  { label: '4 H', shortLabel: '4H', key: '4h', getValue: addHours(4) },
  { label: '6 H', shortLabel: '6H', key: '6h', getValue: addHours(6) },
  { label: '12 H', shortLabel: '12H', key: '12h', getValue: addHours(12) },
  { label: 'End of Day (5 AM)', shortLabel: 'EOD', key: 'endOfDay', getValue: () => nextFiveAM() },
  { label: '24 Hours', shortLabel: '24H', key: 'tomorrow', getValue: addHours(24) },
  { label: '1 Week', shortLabel: '1W', key: '1week', getValue: addDays(7) },
  // Fixed day counts rather than calendar months: "2 Months" was 59-62 days and
  // could never fully deliver, and the button should say exactly what it gives.
  { label: '30 Days', shortLabel: '30D', key: '30days', getValue: addDays(30) },
  { label: '60 Days', shortLabel: '60D', key: '60days', getValue: addDays(MAX_SNOOZE_DAYS) },
];

/**
 * Preset pre-selected when the desktop / bulk snooze dialog opens. Mobile
 * deliberately starts with NO duration selected — its confirm button used to
 * sit in a pinned footer, so users tapped Snooze before scrolling to the
 * duration and got an accidental end-of-day snooze.
 */
export const DEFAULT_ITEM_SNOOZE_PRESET_KEY = 'endOfDay';

export const DEFAULT_ITEM_SNOOZE_PRESET: ItemSnoozePreset = ITEM_SNOOZE_PRESETS.find(
  (p) => p.key === DEFAULT_ITEM_SNOOZE_PRESET_KEY
)!;
